import React from 'react';
import { Box, IconProps } from '@carta/ink';
import SideNavIcon from '../SideNavIcon/SideNavIcon';
import SideNavTwiddle from '../SideNavTwiddle/SideNavTwiddle';
import {
  StyledSideNavGroupToggle,
  StyledSideNavGroupToggleLink,
  StyledSideNavGroupToggleAnchor,
  StyledSideNavGroupToggleLabel,
} from './styles';

export type SideNavGroupToggleProps = {
  /** Modifier for always showing child menu items */
  alwaysShowMenuItems?: boolean;
  /** Modifier for showing child menu items */
  showMenuItems?: boolean;
  /** Group title text */
  title: string;
  /** Group toggle icon type */
  icon?: IconProps['type'];
  /** Badged state modifier */
  isBadged?: boolean;
  /** Active state modifier */
  isActive?: boolean;
  /** Modifier for when a child item is active */
  hasActiveChild?: boolean;
  /** onClick callback, sends the event */
  onClick?: () => void;
  /** URL to navigate to when clicked (optional) */
  url?: string;
  /** Opens link in a new tab */
  openInNewTab?: boolean;
  /** Use React Router/Gatsby Link instead of anchor tag */
  withReactRouter?: boolean;
  /** Nesting level for indentation (0 = root, 1 = first level, 2 = second level) */
  nestingLevel?: 0 | 1 | 2;
};

const SideNavGroupToggle = ({
  onClick,
  alwaysShowMenuItems = false,
  icon,
  showMenuItems = false,
  title,
  isBadged = false,
  isActive = false,
  hasActiveChild = false,
  url,
  openInNewTab,
  withReactRouter,
  nestingLevel = 0,
}: SideNavGroupToggleProps) => {
  const content = (
    <>
      <Box as="span" display="flex" alignItems="center">
        {icon && <SideNavIcon icon={icon} isBadged={isBadged} isActive={isActive} meaning={title} />}
        <StyledSideNavGroupToggleLabel key="title">{title}</StyledSideNavGroupToggleLabel>
      </Box>
      {!alwaysShowMenuItems && <SideNavTwiddle open={showMenuItems} />}
    </>
  );

  // If URL is provided and we're using React Router (Gatsby Link)
  if (url && withReactRouter) {
    return (
      <Box display="flex">
        <StyledSideNavGroupToggleLink
          to={url}
          onClick={onClick}
          alwaysShowMenuItems={alwaysShowMenuItems}
          isActive={isActive}
          hasActiveChild={hasActiveChild}
          nestingLevel={nestingLevel}
        >
          {content}
        </StyledSideNavGroupToggleLink>
      </Box>
    );
  }

  // If URL is provided and it's a regular anchor
  if (url) {
    return (
      <Box display="flex">
        <StyledSideNavGroupToggleAnchor
          href={url}
          target={openInNewTab ? '_blank' : undefined}
          rel={openInNewTab ? 'noopener noreferrer' : undefined}
          onClick={onClick}
          alwaysShowMenuItems={alwaysShowMenuItems}
          isActive={isActive}
          hasActiveChild={hasActiveChild}
          nestingLevel={nestingLevel}
        >
          {content}
        </StyledSideNavGroupToggleAnchor>
      </Box>
    );
  }

  // Default button behavior (for toggle)
  return (
    <Box display="flex">
      <StyledSideNavGroupToggle
        onClick={onClick}
        alwaysShowMenuItems={alwaysShowMenuItems}
        isActive={isActive}
        hasActiveChild={hasActiveChild}
        nestingLevel={nestingLevel}
      >
        {content}
      </StyledSideNavGroupToggle>
    </Box>
  );
};

SideNavGroupToggle.displayName = 'SideNav.GroupToggle';

export default SideNavGroupToggle;
